/**
 * Progresso de uma importação em massa — barra + contadores + amostra dos erros.
 *
 * A mesma vista serve a importação de UMA entidade (`ImportarPlanilha`) e a global (várias
 * entidades por linha): o que o back grava na `Importacao` é o mesmo em ambas.
 */
import { CheckCircle2, XCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/misc';
import type { EstadoImportacao } from '@/lib/api';

function BarraProgresso({ feito, total }: { feito: number; total: number }) {
  const pct = total > 0 ? Math.round((feito / total) * 100) : 0;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="h-2.5 w-full overflow-hidden rounded-full bg-surface-hover">
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-500 ease-(--ease)"
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="tipo-rotulo text-text-tertiary">
        {feito}/{total} linhas · {pct}%
      </p>
    </div>
  );
}

function Contador({ rotulo, valor, tom }: { rotulo: string; valor: number; tom?: 'perigo' }) {
  return (
    <div className="flex flex-col rounded-md border border-border bg-input px-3 py-2">
      <span
        className={`text-xl font-semibold ${tom === 'perigo' && valor > 0 ? 'text-danger' : 'text-text'}`}
      >
        {valor}
      </span>
      <span className="tipo-rotulo text-text-tertiary">{rotulo}</span>
    </div>
  );
}

export function ProgressoImportacao({
  titulo,
  estado,
  rotuloVinculos = 'Vínculos',
  acoes,
}: {
  titulo: string;
  estado: EstadoImportacao | null;
  rotuloVinculos?: string;
  /** Botões do rodapé — só aparecem quando a importação termina (concluída ou erro). */
  acoes?: ReactNode;
}) {
  const terminou = estado?.estado === 'concluida' || estado?.estado === 'erro';
  return (
    <Card>
      <CardHeader>
        <CardTitle>{titulo}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        {!estado ? (
          <div className="flex items-center gap-2 text-text-secondary">
            <Spinner /> Iniciando…
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              {estado.estado === 'concluida' ? (
                <CheckCircle2 className="size-5 text-success" />
              ) : estado.estado === 'erro' ? (
                <XCircle className="size-5 text-danger" />
              ) : (
                <Spinner />
              )}
              <span className="tipo-corpo text-text">{estado.mensagem ?? 'Processando…'}</span>
            </div>

            <BarraProgresso feito={estado.processadas} total={estado.total_linhas} />

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
              <Contador rotulo="Criadas" valor={estado.criadas} />
              <Contador rotulo="Atualizadas" valor={estado.reusadas} />
              <Contador rotulo="Contatos" valor={estado.contatos} />
              <Contador rotulo={rotuloVinculos} valor={estado.socios} />
              <Contador rotulo="Erros" valor={estado.erros} tom="perigo" />
            </div>

            {estado.erros_detalhe.length > 0 && (
              <div className="flex flex-col gap-1 rounded-md border border-border bg-input p-3">
                <span className="tipo-rotulo text-text-secondary">
                  Avisos e linhas com problema
                </span>
                <ul className="flex flex-col gap-0.5">
                  {estado.erros_detalhe.slice(0, 10).map((e) => (
                    <li key={e} className="tipo-rotulo text-text-tertiary">
                      {e}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {terminou && <div className="flex justify-end gap-2">{acoes}</div>}
          </>
        )}
      </CardContent>
    </Card>
  );
}
